// Module registry — single source of truth for the modular intelligence layer.
// Add a new module by appending one entry. Everything else is derived.

export interface ModuleConfig {
  /** Stable key persisted in DB rows. Lowercase, no spaces. */
  key: string;
  /** Human-readable label for UI. */
  label: string;
  /** Short subtitle / tagline for the dashboard hero. */
  tagline?: string;
  /** Topic vocabulary used by the classifier for this module. */
  topics: readonly string[];
  /**
   * Optional module-specific entity subtype vocabulary, persisted into
   * entities.subtype when an entity is module-scoped (e.g., capital VC/PE).
   * Entities themselves are global; subtype is informational metadata.
   */
  entitySubtypes?: readonly string[];
}

export const MODULES = [
  {
    key: "media",
    label: "Black Media Intelligence",
    tagline: "Real activity vs recycled content, PR, and noise.",
    topics: [
      "publishing",
      "broadcast",
      "streaming",
      "podcasts",
      "advertising",
      "audience",
      "ownership",
      "creator-economy",
      "distribution",
      "newsroom",
      "policy",
    ],
  },
  {
    key: "capital",
    label: "Black Capital Intelligence",
    tagline: "Where capital is actually moving — not where it's being announced.",
    topics: [
      "venture",
      "private-equity",
      "public-markets",
      "fund-formation",
      "lp-allocation",
      "diligence",
      "exit",
      "policy",
      "small-business",
      "real-estate",
      "credit",
    ],
    entitySubtypes: ["VC", "PE", "BANK", "CDFI", "FOUNDATION", "NETWORK"],
  },
  {
    key: "education",
    label: "Black Education Intelligence",
    tagline: "What's actually happening in Black education — not what gets announced.",
    topics: [
      "hbcu",
      "k-12",
      "higher-ed",
      "charter-schools",
      "public-schools",
      "accreditation",
      "funding",
      "policy",
      "curriculum",
      "equity",
      "workforce-pipeline",
    ],
    entitySubtypes: [
      "HBCU",
      "UNIVERSITY",
      "K12_DISTRICT",
      "CHARTER",
      "DOE",
      "EDTECH",
      "ADVOCACY",
    ],
  },
  {
    key: "policy",
    label: "Black Policy Intelligence",
    tagline: "What policy is actually doing — not what's being announced.",
    topics: [
      "legislation",
      "budget",
      "grants",
      "regulation",
      "executive-action",
      "governance",
    ],
    entitySubtypes: [
      "AGENCY",
      "LEGISLATOR",
      "REGULATORY_BODY",
      "POLICY_ORG",
      "ADVOCACY",
    ],
  },
  {
    key: "workforce",
    label: "Black Workforce Intelligence",
    tagline:
      "What is actually happening in jobs, wages, and mobility — not what is being announced.",
    topics: [
      "hiring",
      "layoffs",
      "job_placement",
      "wages",
      "entrepreneurship",
      "gig_economy",
      "workforce_programs",
    ],
    entitySubtypes: [
      "EMPLOYER",
      "PROGRAM",
      "TRAINING_PROVIDER",
      "GOVERNMENT_AGENCY",
      "COMPANY",
      "UNION",
      "STAFFING_FIRM",
      "NONPROFIT",
    ],
  },
  {
    key: "legal",
    label: "Black Legal Intelligence",
    tagline: "Legal actions, enforcement, court decisions, and justice outcomes affecting Black communities.",
    topics: [
      "policing",
      "sentencing",
      "voting_rights",
      "civil_rights",
      "discrimination",
      "housing_law",
      "labor_law",
      "criminal_justice",
      "lawsuits",
      "enforcement",
    ],
    entitySubtypes: [
      "COURT",
      "JUDGE",
      "LAW_FIRM",
      "AGENCY",
      "PROSECUTOR",
      "POLICE_DEPARTMENT",
      "CIVIL_RIGHTS_GROUP",
    ],
  },
] as const satisfies readonly ModuleConfig[];

/** Union type derived from the registry. Adding a module to MODULES extends this. */
export type ModuleKey = (typeof MODULES)[number]["key"];

export const MODULE_KEYS: readonly ModuleKey[] = MODULES.map((m) => m.key) as ModuleKey[];

export const MODULES_BY_KEY: Record<string, ModuleConfig> = Object.fromEntries(
  MODULES.map((m) => [m.key, m]),
);

export function isModuleKey(value: string): value is ModuleKey {
  return value in MODULES_BY_KEY;
}

export function getModule(key: string): ModuleConfig | null {
  return MODULES_BY_KEY[key] ?? null;
}

export function topicsFor(key: string): readonly string[] {
  return MODULES_BY_KEY[key]?.topics ?? [];
}

export function entitySubtypesFor(key: string): readonly string[] {
  return MODULES_BY_KEY[key]?.entitySubtypes ?? [];
}
